import { useAuth0 } from '@auth0/auth0-react'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { localStorageItemKeys } from '~constants/local-storage-items'
import { DateFormatOption } from '~pages/pages-behind-login/profile-page/sub-components/date-card'

export class ApiRequestError extends Error {
  statusCode: number
  apiMessage?: string

  constructor(statusCode: number, apiMessage?: string) {
    super('something went wrong')
    this.name = 'ApiRequestError'
    this.statusCode = statusCode
    this.apiMessage = apiMessage
  }
}

async function readApiMessage(response: Response): Promise<string | undefined> {
  try {
    const body: { message?: string } = await response.json()
    return body.message
  } catch {
    return undefined
  }
}

export function useFetch<T = unknown>() {
  const { getAccessTokenSilently } = useAuth0()
  const { setStatus } = useAnimatedAlert()

  async function fetchRequest(
    requestUrl: string,
    options?: RequestInit,
    payload?: unknown,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET',
    formData?: FormData,
    customHeaders?: Record<string, string>,
  ): Promise<T> {
    const token = await getAccessTokenSilently()
    const impersonate: DateFormatOption | undefined = localStorage.getItem(
      localStorageItemKeys.impersonate,
    )
      ? JSON.parse(
          localStorage.getItem(localStorageItemKeys.impersonate) as string,
        )
      : undefined

    const earningsValidation: boolean | undefined = localStorage.getItem(
      localStorageItemKeys.earningsValidation,
    )
      ? JSON.parse(
          localStorage.getItem(
            localStorageItemKeys.earningsValidation,
          ) as string,
        )
      : undefined

    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
      ...customHeaders,
    }

    if (impersonate?.id) {
      headers['x-impersonate'] = impersonate.id
    }

    if (earningsValidation) {
      headers['x-earningsvalidation'] = 'true'
    }

    if (!formData) {
      headers['Content-Type'] = 'application/json'
    }

    let response: Response
    try {
      response = await fetch(requestUrl, {
        method,
        headers,
        ...options,
        body: formData || JSON.stringify(payload),
      })
    } catch {
      setStatus('Network error - please check your connection', 5000, 'error')
      throw new Error('network error')
    }

    if (response.status === 401 || response.status === 403) {
      setStatus("You're not authorized to perform this action.", 5000, 'error')
      await new Promise((resolve) => setTimeout(resolve, 5000))
      throw new Error('unauthorized')
    } else if (!response.ok) {
      throw new ApiRequestError(response.status, await readApiMessage(response))
    }
    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      return response.json()
    } else {
      return response.text() as unknown as T
    }
  }

  return fetchRequest
}
